import { FocusMonitor } from "@angular/cdk/a11y";
import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NgControl,
  ValidatorFn
} from "@angular/forms";
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
} from "@angular/material/autocomplete";
import { MatChipInputEvent } from "@angular/material/chips";
import {
  MAT_FORM_FIELD,
  MatFormField,
  MatFormFieldControl,
} from "@angular/material/form-field";
import { IModel, IPaginator, Paginator } from "maps-angular-components";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, map, switchMap, tap } from "rxjs/operators";
import { Autocomplete } from "src/app/generic-autocomplete/autocomplete";
import { ChipList, IAutocompleteSchema } from "../autocomplete.schema";

@Component({
  selector: "chip-autocomplete-input",
  templateUrl: "./chip-autocomplete.input.html",
  providers: [
    { provide: MatFormFieldControl, useExisting: ChipAutocompleteInput },
  ],
  host: {
    "[class.example-floating]": "shouldLabelFloat",
    "[id]": "id",
  },
})
export class ChipAutocompleteInput<T extends IModel>
  implements
    ControlValueAccessor,
    MatFormFieldControl<ChipList<T>>,
    OnInit,
    OnDestroy,
    AfterViewInit
{
  @Input() schema: IAutocompleteSchema<T>;

  @Output() throwError: EventEmitter<string> = new EventEmitter();

  static nextId = 0;

  @ViewChild("chipInput") chipInput: ElementRef<HTMLInputElement>;
  @ViewChild("auto") matAutocomplete: MatAutocomplete;
  @ViewChild("except") exceptInput: HTMLInputElement;

  parts: FormGroup;
  stateChanges = new Subject<void>();
  focused = false;
  touched = false;

  controlType = "chip-autocomplete-input";
  id = `chip-autocomplete-input-${ChipAutocompleteInput.nextId++}`;
  onChange = (_: any) => {};
  onTouched = () => {};

  public inputFormControl = new FormControl();

  public pagination: Paginator<T>;
  private $paginator: BehaviorSubject<Paginator<T>> = new BehaviorSubject<Paginator<T>>(null);

  public selectedOptions$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  public filteredOptions$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);

  separatorKeysCodes: number[] = [ENTER, COMMA];

  public loading$: Subject<boolean> = new Subject<boolean>();

  get empty() {
    const {
      value: { items, except },
    } = this.parts;

    return !items || items.length === 0;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input() formGroup = new FormGroup({});

  get errorState(): boolean {
    return this.parts.invalid && this.touched;
  }

  @Input() value: any = null;

  constructor(
    private formBuilder: FormBuilder,
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
    @Optional() @Self() public ngControl: NgControl
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  private chipValidator(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {
      const valid = control.get("items").value?.length > 0;
      return valid ? null : { valorMenorZero: !valid };
    };
  }

  ngOnInit(): void {
    this.schema.formGroup.valueChanges.subscribe(res => {
      this.disableIfDependenciesHasNoValues();
    })

    if (this.schema.pagination) {
      this.pagination = new Paginator(this.schema.pagination);
    } else {
      this.pagination = new Paginator({});
    }

    if (!this.pagination.filters) this.pagination.filters = {};

    this.parts = this.formBuilder.group(
      {
        items: [null],
        except: [null],
      },
      {
        validators: this.schema.required ? [this.chipValidator()] : null,
      }
    );
  }

  ngAfterViewInit() {

    this.parts.patchValue(this.value, { emitEvent: true })

    this.disableIfDependenciesHasNoValues();
    this.trackStateChanges();
    // this.trackDependenciesChanges();
    this.trackSelectedOptions();
    this.trackInputChanges();
    this.trackPaginatorChanges();

    this.selectedOptions$.next(this.value?.items?.map(res => new Autocomplete(res)));
    this.schema.formGroup.setControl(this.schema.formControlName, this.parts);
  }

  private trackStateChanges() {
    this.stateChanges.subscribe((_) => {
      if (this._disabled && !this.exceptInput.disabled) {
        this.inputFormControl.disable();
        this.exceptInput.disabled = true;
      } else if (!this._disabled && this.exceptInput.disabled) {
        this.inputFormControl.enable();
        this.exceptInput.disabled = false;
      }
    });
  }

  private trackDependenciesChanges() {
    this.schema.formDependentFilters.forEach((d) => {
      this.schema.formGroup.get(d).valueChanges.subscribe((v) => {
        this.reset();
        this.disableIfDependenciesHasNoValues();
        this.pagination!.filters = {
          ...this.pagination!.filters,
          ...this.schema.formGroup.get(d).value,
        };
      });
    });
  }

  private disableIfDependenciesHasNoValues() {
    this.disabled = this.schema.readonly || this.schema.formDependentFilters.some((d) => {
      const value = this.schema.formGroup.get(d).value;
      return !value || !value[d];
    });
  }

  private trackSelectedOptions() {
    this.selectedOptions$.subscribe(s => {
      if (s === undefined)
        return;

      this.parts.setValue({
        items: s,
        except: this.value ? this.value.except : null,
      });

    });
  }

  private trackInputChanges() {
    this.inputFormControl.valueChanges.subscribe((value) => {
      this.pagination.search = value;

      if (
        this.filteredOptions$
          ?.getValue()
          ?.filter((a) => a.getName() === this.pagination?.search).length === 1
      ) {
        return;
      }

      if (!value || value.length < 3) {
        this.filteredOptions$ = new BehaviorSubject<T[]>([]);
        return;
      }

      this.paginate(this.pagination);
    });
  }

  private trackPaginatorChanges() {
    this.$paginator
      .pipe(
        filter(page => page != null),
        tap(_ => this.loading$.next(true)),
        switchMap(page =>
          this.schema.service.autocomplete(
            page.search,
            this.schema.endpoint,
            page.filters
          )
        ),
        map(res => this.schema.parsePaginatedResponse(res))
      )
      .subscribe(
        res => {
          this.loading$.next(false);
          this.filteredOptions$.next(
            res.data?.filter(
              item => !this.selectedOptions$.getValue()?.map(i => i.getId()).includes(item.getId())
            )
          );
        },
        err => {
          this.loading$.next(false);
          console.log(err);
          this.throwError.emit(err);
        }
      );
  }

  exceptChanged(checked: boolean) {
    this.parts.setValue({
      items: this.selectedOptions$.getValue(),
      except: checked,
    });
  }

  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (
      !this._elementRef.nativeElement.contains(event.relatedTarget as Element)
    ) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  autoFocusNext(
    control: AbstractControl,
    nextElement?: HTMLInputElement
  ): void {
    if (!control.errors && nextElement) {
      this._focusMonitor.focusVia(nextElement, "program");
    }
  }

  autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
    if (control.value.length < 1) {
      this._focusMonitor.focusVia(prevElement, "program");
    }
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector(
      ".example-tel-input-container"
    )!;
    controlElement.setAttribute("aria-describedby", ids.join(" "));
  }

  onContainerClick() {
    // if (this.parts.controls.search.valid) {
    //   this._focusMonitor.focusVia(this.chipInput, 'program');
    // }
  }

  writeValue(chips: ChipList<T> | null): void {
    this.value = chips;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  _handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
    this.autoFocusNext(control, nextElement);
    this.onChange(this.value);
  }

  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_required: BooleanInput;

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || "").trim()) {
      this.selectedOptions$.next(
        this.selectedOptions$
          .getValue()
          ?.concat(
            this.filteredOptions$
              .getValue()
              ?.filter((v) => v.getName() === value.trim())
          )
      );
    }

    if (input) {
      input.value = "";
    }

    this.inputFormControl.setValue(null);
  }

  remove(item: T): void {
    this.selectedOptions$.next(
      this.selectedOptions$
        .getValue()
        ?.filter((i) => i.getName() !== item.getName())
    );
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (this.selectedOptions$.getValue() == undefined) this.selectedOptions$.next(this.filteredOptions$.getValue()?.filter((v) => v.getName() === event.option.viewValue));
    else this.selectedOptions$.next(this.selectedOptions$.getValue()?.concat(this.filteredOptions$.getValue()?.filter((v) => v.getName() === event.option.viewValue)));

    this.chipInput.nativeElement.value = "";
    this.inputFormControl.setValue(null);
  }

  reset() {
    this.selectedOptions$.next([]);
    this.filteredOptions$.next([]);
    this.inputFormControl.reset();
    this.inputFormControl.markAsPending();
  }

  private paginate(pagination: IPaginator): void {
    if (!pagination && this.schema.pagination) {
      pagination = this.schema.pagination;
    }

    this.$paginator.next(new Paginator(pagination));
  }
}
